import React, { useState, useEffect, useRef } from 'react';
import useAuth from 'hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, TextField } from '@mui/material';
import PasswordInputField from 'components/atoms/PasswordInputField/PasswordInputField';
import ConfirmPasswordInputField from 'components/atoms/ConfirmPasswordInputField/ConfirmPasswordInputField';
import BirthdayDate from 'components/atoms/BirthdayDate/BirthdayDate';
import Heading from 'components/atoms/Heading/Heading';
import Paragraph from 'components/atoms/Paragraph/Paragraph';
import Breadcrumbs from 'components/atoms/Breadcrumbs/Breadcrumbs';
import RedirectComponent from 'components/atoms/RedirectComponent/RedirectComponent';
import { Message } from 'components/atoms/Message/Message';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import axiosInstance from 'api/axios';
import { apiRoutes } from 'api_routes/api_routes';
import { theme } from 'theme/MainTheme';
import { useTranslation } from 'react-i18next';

const StyledMessage = styled(Message)`
  padding-top: 5px;
  font-size: ${theme.fontSize.xxs};
  color: ${theme.primary};
  font-weight: ${theme.bold};
`;

const StyledHeading = styled(Heading)`
  text-align: center;
  font-size: 2rem;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: -10px;
  color: ${theme.red};
  font-size: ${theme.fontSize.s};
  font-weight: ${theme.bold};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledForm = styled.form`
  .MuiFormLabel-root,
  .MuiInputLabel-shrink,
  #username-label,
  #password-label {
    font-size: 1.4rem;
  }
`;

function LoginFirst() {
  const { setAuth } = useAuth();
  const { t } = useTranslation();
  const errRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [userData, setUserData] = useState({ smsCode: '' });
  const [workflow, setWorkflow] = useState({});

  const [passwordError, setPasswordErr] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [passwordInput, setPasswordInput] = useState({ password: '', confirmPassword: '' });
  const [errMsg, setErrMsg] = useState('');
  const smsCodeRef = useRef(null);
  const [reminderKeySend, isReminderKeySend] = useState(true);

  useEffect(() => {
    smsCodeRef.current?.focus();
  }, []);

  useEffect(() => {
    setErrMsg('');
  }, [userData.geid, passwordInput]);

  const birthdayDateChangeHandler = (date) => {
    setUserData({ ...userData, birthdate: date });
  };

  const handleGeidChange = (e) => {
    setUserData({ ...userData, geid: e.target.value });
  };

  const handleCodeChange = (event) => {
    setUserData({ ...userData, smsCode: event.target.value });
  };

  const handlePasswordChange = (e) => {
    const passwordInputValue = e.target.value.trim();
    const passwordInputFieldName = e.target.name;
    const NewPasswordInput = { ...passwordInput, [passwordInputFieldName]: passwordInputValue };
    setPasswordInput(NewPasswordInput);
  };

  const handleValidation = (e) => {
    const passwordInputValue = e.target.value.trim();
    const passwordInputFieldName = e.target.name;

    if (passwordInputFieldName === 'password') {
      const uppercaseRegExp = /(?=.*?[A-Z])/;
      const lowercaseRegExp = /(?=.*?[a-z])/;
      const digitsRegExp = /(?=.*?[0-9])/;
      const minLengthRegExp = /.{8,}/;
      const passwordLength = passwordInputValue.length;
      const uppercasePassword = uppercaseRegExp.test(passwordInputValue);
      const lowercasePassword = lowercaseRegExp.test(passwordInputValue);
      const digitsPassword = digitsRegExp.test(passwordInputValue);
      const minLengthPassword = minLengthRegExp.test(passwordInputValue);
      let errMsg2 = '';

      if (passwordLength === 0) {
        errMsg2 = t('password_empty');
      } else if (!uppercasePassword) {
        errMsg2 = t('one_capital_letter');
      } else if (!lowercasePassword) {
        errMsg2 = t('one_lowercase_letter');
      } else if (!digitsPassword) {
        errMsg2 = t('one_digit');
      } else if (!minLengthPassword) {
        errMsg2 = t('eight_characters');
      } else errMsg2 = '';

      setPasswordErr(errMsg2);
    }
    checkValidation();
  };

  const checkValidation = () => {
    if (
      passwordError === '' &&
      passwordInput.password !== '' &&
      passwordInput.confirmPassword !== ''
    ) {
      if (passwordInput.password === passwordInput.confirmPassword) {
        setWorkflow({ ...workflow, isPasswordOk: true });
        setConfirmPasswordError('');
      } else {
        setWorkflow({ ...workflow, isPasswordOk: false });
        setConfirmPasswordError(t('password_no_match'));
      }
    } else {
      setWorkflow({ ...workflow, isPasswordOk: false });
    }
  };

  const validate = () => {
    if (!userData.geid) {
      return t('please_give_geid');
    }
    if (!userData.birthdate) {
      return t('please_give_birth_date');
    }
    return null;
  };

  const sendUserData = () => {
    const errorMsg = validate(userData);
    if (errorMsg) {
      setErrMsg(errorMsg);
      return;
    }
    axiosInstance
      .get(apiRoutes.send_sms_code, {
        params: {
          id: userData.geid,
          birthdate: userData.birthdate.toISOString().split('T')[0],
          msg: t('sms_api_first_login_msg'),
        },
      })
      .then((response) => {
        setUserData({
          ...userData,
          phone: response.data.phone,
          smsCode: '',
        });
        setWorkflow({
          ...workflow,
          isUserDataSend: true,
          isSmsSend: true,
          isSmsCodeOk: false,
          isNewSmsCode: false,
        });
        setErrMsg('');
      })
      .catch((err) => {
        if (err.response) {
          if (!err?.response) {
            setErrMsg(t('no_serwer_response_inf'));
          } else
            switch (err.response?.status) {
              case 400:
                setErrMsg(t('geid_birth_err_inf'));
                break;
              case 401:
                setErrMsg(t('no_match_data'));
                break;
              case 403:
                setErrMsg(`${t('no_first_login_user')}`);
                localStorage.setItem('user', userData.geid);
                setWorkflow({ ...workflow, isCounter: true });
                break;
              case 425:
                setErrMsg(t('account_blocked_24'));
                break;
              case 503:
                setErrMsg(t('no_phone_info'));
                break;
              default:
                setErrMsg(t('login_err_inf'));
                break;
            }
        } else if (err.request) {
          if (userData.birthdate && userData.geid) {
            setErrMsg(t('no_match_data'));
          }
          if (!userData.geid && userData.birthdate) {
            setErrMsg(t('please_give_geid'));
          }
          if (!userData.birthdate && userData.geid) {
            setErrMsg(t('please_give_birth_date'));
          }
          if (!userData.birthdate && !userData.geid) {
            setErrMsg(t('please_give_geid_birth_date'));
          }
        }
      });
  };

  const werifySmsCode = () => {
    axiosInstance
      .post(apiRoutes.login_standard, {
        geid: userData.geid,
        smsCode: userData.smsCode,
      })
      .then((res) => {
        setUserData({ ...userData, firstname: res.data.firstname, lastname: res.data.lastname });
        setWorkflow({ ...workflow, isSmsCodeOk: true, isSmsSend: false });
        setErrMsg('');
        if (res.data.notificationResult.code === 400) {
          isReminderKeySend(false);
        }
      })
      .catch((err) => {
        if (err.response) {
          if (!err?.response) {
            setErrMsg(t('no_serwer_response_inf'));
          } else
            switch (err.response?.status) {
              case 400:
                setErrMsg(t('give_sms_code'));
                break;
              case 404:
                setErrMsg(t('sms_code_not_send'));
                break;
              case 406:
                setErrMsg(t('sms_code_not_walid'));
                localStorage.setItem('user', userData.geid);
                setWorkflow({ ...workflow, isNewSmsCode: true });
                break;
              case 423:
                setErrMsg(t('account_inactive'));
                break;
              case 425:
                setErrMsg(t('account_blocked_24'));
                break;
              case 428:
                setErrMsg(t('incorrect_sms_code'));
                break;
              case 403:
                setErrMsg(`${t('no_first_login_user')}`);
                setWorkflow({ ...workflow, isCounter: true });
                break;
              default:
                setErrMsg(t('login_err_inf'));
                break;
            }
        }
      });
  };

  const onSubmit = () => {
    axiosInstance
      .post(apiRoutes.password_update, {
        geid: userData.geid,
        password: passwordInput.password,
      })
      .then((res) => {
        if (res.status === 204) {
          logIn();
        }
      })
      .catch((err) => {
        if (err.response) {
          if (!err?.response) {
            setErrMsg(t('no_serwer_response_inf'));
          } else {
            setErrMsg(t('update_password_fail'));
          }
        }
      });
  };

  const logIn = async (e) => {
    try {
      const response = await axiosInstance.post(
        apiRoutes.login_standard,
        JSON.stringify({ geid: userData.geid, password: passwordInput.password }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        },
      );
      const roles = response?.data?.roles;
      localStorage.setItem('user', userData.geid);
      localStorage.setItem('roles', JSON.stringify(roles));
      setAuth({
        user: userData.geid,
        pwd: passwordInput.password,
        roles,
      });
      setPasswordInput('');
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.response) {
        setErrMsg(t('no_serwer_response_inf'));
      } else if (err.response?.status === 400) {
        setErrMsg(t('geid_birth_err_inf'));
      } else if (err.response?.status === 401) {
        setErrMsg(t('no_authorization_inf'));
      } else {
        setErrMsg(t('login_err_inf'));
      }
      errRef.current.focus();
    }
  };

  return (
    <StyledForm onSubmit={onSubmit}>
      <StyledHeading>{t('first_login')}</StyledHeading>
      {errMsg && (
        <Stack sx={{ maxWidth: '400px' }} spacing={0}>
          <Alert
            severity="error"
            sx={{ alignItems: 'center', fontSize: '1rem', height: 'fit-content' }}
          >
            {errMsg}
            {workflow?.isCounter && <RedirectComponent />}
          </Alert>
        </Stack>
      )}
      {!reminderKeySend && (
        <Stack sx={{ maxWidth: '400px' }} spacing={0}>
          <Alert
            severity="warning"
            sx={{ alignItems: 'center', fontSize: '1rem', height: 'fit-content' }}
          >
            {t('key_reminder_welcome')}
          </Alert>
        </Stack>
      )}
      <TextField
        autoFocus
        margin="normal"
        variant="standard"
        id="name"
        label="GEID"
        type="text"
        fullWidth
        required
        name="geid"
        autoComplete="off"
        onChange={handleGeidChange}
      />
      <BirthdayDate
        name="birthdate"
        value={userData.birthdate}
        onChange={birthdayDateChangeHandler}
        sx={{ margin: '0 auto' }}
      />
      {!workflow.isUserDataSend && (
        <ButtonWrapper>
          <Button
            type="button"
            variant="contained"
            sx={{ backgroundColor: theme.primary, mb: 1, mt: 1.5, width: '150px' }}
            onClick={sendUserData}
          >
            {t('send')}
          </Button>
          <Button
            type="button"
            variant="contained"
            sx={{ backgroundColor: theme.primary, mb: 1, mt: 0, width: '150px' }}
            onClick={() => navigate('/login')}
          >
            {t('account_exist')}
          </Button>
        </ButtonWrapper>
      )}

      {workflow.isSmsSend && (
        <div>
          <TextField
            autoFocus
            variant="standard"
            id="sms_code"
            label={t('sms_code')}
            type="text"
            fullWidth
            required
            inputRef={smsCodeRef}
            name="sms_code"
            value={userData.smsCode}
            onChange={handleCodeChange}
            sx={{ mb: 1, mt: 1 }}
          />
          <Message>
            {t('give_send_sms_code')}
            {userData.phone ? userData?.phone.slice(-3) : ''}.{t('give_send_sms_code2')}
          </Message>
          <ButtonWrapper>
            <Button
              type="button"
              variant="contained"
              sx={{ backgroundColor: theme.primary, mb: 1, mt: 1.5, width: '150px' }}
              onClick={werifySmsCode}
            >
              {t('confirm')}
            </Button>
            {workflow.isNewSmsCode && (
              <Button
                type="button"
                variant="contained"
                sx={{ backgroundColor: theme.primary, mb: 1, mt: 0, width: '150px' }}
                onClick={sendUserData}
              >
                Nowy kod SMS
              </Button>
            )}
          </ButtonWrapper>
        </div>
      )}
      {workflow.isSmsCodeOk && (
        <>
          <StyledParagraph center>
            {userData.firstname} {userData.lastname}
          </StyledParagraph>
          <PasswordInputField
            id="password"
            type="password"
            label={t('new_password')}
            handlePasswordChange={handlePasswordChange}
            handleValidation={handleValidation}
            passwordValue={passwordInput.password}
            passwordError={passwordError}
          />
          <ConfirmPasswordInputField
            id="passwordConfirm"
            type="password"
            label={t('confirm_password')}
            handlePasswordChange={handlePasswordChange}
            handleValidation={handleValidation}
            confirmPasswordValue={passwordInput.confirmPassword}
            confirmPasswordError={confirmPasswordError}
          />
          <StyledMessage>{t('password_walidation_info')}</StyledMessage>
          <ButtonWrapper>
            <Button
              type="button"
              variant="contained"
              sx={{ backgroundColor: theme.primary, mb: 1, mt: 1.5, width: '150px' }}
              disabled={!workflow.isPasswordOk}
              onClick={onSubmit}
            >
              {t('confirm')}
            </Button>
          </ButtonWrapper>
        </>
      )}
      {!workflow.isUserDataSend && (
        <Breadcrumbs>
          <span>GEID:</span> {t('geid_definition')} {t('geid_definition_disclaimer')}
        </Breadcrumbs>
      )}
    </StyledForm>
  );
}

export default LoginFirst;
